import * as echarts from 'echarts';
import { L } from '@/plugins/utils/i18n';

import { getColor, seconds2HHmm, formatDateString } from '@/plugins/utils/utils';
const catColor = ['#36349d', '#3478f6', '#59aae0', '#fe8973', '#CCC', '#8E377A', '#C9D1DC'];

const minSegmentWidth = 5;
const SIGNAL_MISSING_STAGE_INDEX = 6;
const MIN_SIGNAL_MISSING_SECONDS_DEFAULT = 10 * 60;
const SIGNAL_MISSING_GAP_TOLERANCE = 60; // 间隔 < 60 秒的相邻缺失段视为连续

/**
 * 归一化信号缺失区间：裁剪到报告范围、容差合并相邻段、过滤 <10 分钟的短段
 * 容差合并：两段缺失之间间隔不超过 60 秒时合并为一段（设备补传中会出现
 * "每隔几秒丢几秒"的碎片模式，不做容差合并会导致大面积缺失无法显示）
 */
function normalizeSignalMissingIntervals(intervals, reportDuration, minSeconds) {
    const threshold = Number.isFinite(minSeconds) ? minSeconds : MIN_SIGNAL_MISSING_SECONDS_DEFAULT;
    if (!Array.isArray(intervals) || intervals.length === 0 || !reportDuration || reportDuration <= 0) return [];
    return intervals.map((interval) => {
        if (!interval || !Number.isFinite(interval.s) || !Number.isFinite(interval.e)) return null;
        const s = Math.max(0, interval.s);
        const e = Math.min(reportDuration, interval.e);
        return e > s ? { s, e, dur: e - s } : null;
    }).filter(Boolean).sort((a, b) => a.s - b.s).reduce((merged, cur) => {
        if (merged.length === 0) { merged.push(cur); return merged; }
        const last = merged[merged.length - 1];
        if (cur.s - last.e <= SIGNAL_MISSING_GAP_TOLERANCE) {
            last.e = Math.max(last.e, cur.e);
            last.dur = last.e - last.s;
        } else {
            merged.push(cur);
        }
        return merged;
    }, []).filter((interval) => interval.dur >= threshold);
}

/**
 * 从离床段中减去数据缺失区间，使二者互斥显示
 */
function subtractLeaveBedIntervals(leavebed, missingIntervals) {
    if (!Array.isArray(leavebed)) return [];
    if (!Array.isArray(missingIntervals) || missingIntervals.length === 0) {
        return leavebed
            .filter((seg) => Number.isFinite(seg && seg.s) && Number.isFinite(seg && seg.e) && seg.e > seg.s)
            .map((seg) => ({ s: seg.s, e: seg.e }));
    }
    return leavebed.reduce((result, segment) => {
        if (!Number.isFinite(segment && segment.s) || !Number.isFinite(segment && segment.e) || segment.e <= segment.s) return result;
        let fragments = [{ s: segment.s, e: segment.e }];
        for (const missing of missingIntervals) {
            if (!Number.isFinite(missing && missing.s) || !Number.isFinite(missing && missing.e) || missing.e <= missing.s) continue;
            fragments = fragments.flatMap((fragment) => {
                if (missing.e <= fragment.s || missing.s >= fragment.e) return [fragment];
                const remaining = [];
                if (missing.s > fragment.s) remaining.push({ s: fragment.s, e: Math.min(missing.s, fragment.e) });
                if (missing.e < fragment.e) remaining.push({ s: Math.max(missing.e, fragment.s), e: fragment.e });
                return remaining;
            });
            if (fragments.length === 0) break;
        }
        return result.concat(fragments);
    }, []);
}

export function renderItem(params, api) {
    var curveShape = {
        type: 'rect' //避免animation bug
    };
    var rectShape = {
        type: 'rect'
    };
    var markerShape = {
        type: 'rect'
    };

    var categoryIndex = api.value(0);
    var nextCategoryIndex = api.value(3);
    if (categoryIndex>=0) {
        var start = api.coord([api.value(1), (categoryIndex>3) ? 3 : categoryIndex]);
        var end = api.coord([api.value(2), (categoryIndex>3) ? 3 : categoryIndex]);
        var start2 = nextCategoryIndex!=-1 ? api.coord([api.value(1), (nextCategoryIndex>3) ? 3 : nextCategoryIndex]) : start;
        var end2 = nextCategoryIndex!=-1 ? api.coord([api.value(2), (nextCategoryIndex>3) ? 3 : nextCategoryIndex]) : end;
        var height = api.size([0, 1])[1] * 0.7;
        
        var cond1 = (start[1]>start2[1]);
        var cond2 = (start[1]>=start2[1]);
        var x1 = end[0];
        var y1 = start[1] + ( cond1 ? -height*1/3 : height*2/3);
        var x2 = end2[0];
        var y2 = start2[1] + ( cond2 ? height*2/3 : -height/3);

        var thisColor = catColor[categoryIndex];
        var nextColor = catColor[nextCategoryIndex];
    
        rectShape = {
                type: 'rect',
                x: start[0] - 0.5,              // offset半个像素才能与直线对齐，dont know why..
                y: start[1] - height / 3,
                width: end[0] - start[0] + 1,   //两倍半像素
                height: height
            };
        if (categoryIndex==4) { //卧床无意图
            rectShape = {
                type: 'rect',
                x: start[0],
                y: start[1] - height / 3,
                width: end[0] - start[0],
                height: height
            };
        }
        if (categoryIndex==5 || categoryIndex==SIGNAL_MISSING_STAGE_INDEX) { //离床 / 数据缺失
            //height = api.size([0, 1])[1];
            rectShape = {
                type: 'rect',
                x: start[0],
                y: start[1] - height /3 - ((rectShape.width<minSegmentWidth) ? height*0.1 : 0), //((rectShape.width<minSegmentWidth) ? height*0.8 : height*0.7) / ((rectShape.width<minSegmentWidth) ? 2 : 3),
                width: end[0] - start[0],
                height: (rectShape.width<minSegmentWidth) ? height*1.1 : height,
                z: 10,
            };
            
            if (rectShape.width<minSegmentWidth) {
                let mWidth = 7;
                let mHeight = 6;
                markerShape = {
                    type: 'polygon',
                    shape: {
                        points: [ [start[0] + rectShape.width/2 - mWidth/2, start[1] - rectShape.height/2 - mHeight/2], 
                        [start[0] + rectShape.width/2  + mWidth/2, start[1] - rectShape.height/2 - mHeight/2], 
                        [start[0] + rectShape.width/2 , start[1] - rectShape.height/2 + mHeight/2] ]
                    },
                    /*type: 'circle',
                    shape: {
                        cx: start[0] + rectShape.width/2,
                        cy: start[1] - rectShape.height/2,
                        r: mHeight / 2,
                    },*/
                    style: {
                        fill: thisColor,
                        opacity: 0.8,
                    },
                    emphasis: {
                        style: {
                            stroke: thisColor,
                            lineWidth: 3,
                        }
                    },
                    z: 20,
                }
            }
        }
        
        //下一分段有效，则画连接线
        if (!(categoryIndex>=3 && nextCategoryIndex>=3) && nextCategoryIndex>=0) { 
            curveShape = {
                type: 'line',
                silent: true,
                transition: ['shape'],
                shape: {
                    x1: x1,
                    y1: y1+(cond1?3:-3),
                    x2: x2,
                    y2: y2+(cond1?-3:3),
                },
                style: {
                    stroke: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{
                        offset: 0, color: cond1 ? nextColor : thisColor
                    }, {
                        offset: 1, color: cond1 ? thisColor : nextColor
                    }
                    ]),
                    lineWidth: 1,
                    opacity: 0.7
                }
            };
        }
    }

    let radius = 4;//(rectShape.width > 2) ? 4 : 0;
    return ( {
        type: 'group',
        children: [
            curveShape,
            markerShape,
            rectShape && {
                type: 'rect',
                silent: false,//(categoryIndex>3),
                transition: ['shape'],
                shape: {...rectShape, r: radius },
                style: {...api.style(), 
                    fill: thisColor,
                    opacity: (categoryIndex>3) ? 0.8 : 1,
                },
                //z: 2,
                emphasis: {
                    style: {
                        stroke: thisColor,
                        lineWidth: 3,
                        //shadowBlur: 2,
                    }
                },
            }
        ]
    } );
}

// 睡眠分期合并：5分期合并为4分期
function mergeSleepStage(stage) {
    switch(stage) {
        case -1: return -1; // 无效的数据段
        case 1: return 3;   // WAKE
        case 2: return 2;   // REM
        case 3: return 1;   // Core
        case 4: return 1;   // Core
        case 5: return 0;   // Deep
        default:
            return 3;
    }
}
export function setStageChartOption (stage_chart, stage_data) {
  if (!stage_data || !stage_data.stages) {
    console.log('Empty stage data, set chart fail.')
    return;
  }
  if (!stage_chart) { // 图表组件尚未初始化
    return;
  }

  const data = [];
  const categories = [L.deep_sleep, L.light_sleep, L.stage_axis_rem_sleep, L.wake];

  const baseTime = stage_data.start;
  // 从睡眠分期数组读出绘图数据
  for (let i = 0; i < stage_data.stages.length; i++) {
    // 5分期合并为4分期
    const stg = mergeSleepStage(stage_data.stages[i].value);

    data.push({
      name: stg >= 0 ? categories[stg] : '',
      value: [stg, baseTime * 1000 + stage_data.stages[i].start * 1000, baseTime * 1000 + stage_data.stages[i].end * 1000, -1],
      itemStyle: { color: stg >= 0 ? catColor[stg] : '#FFF' }
    });
  }

  // 分期中的相邻碎片合并
  for (let i = 1; i < data.length; i++) {
    if (data[i - 1].value[0] === data[i].value[0]) {
      data[i - 1].value[2] = data[i].value[2];
      data.splice(i, 1);
      i--;
    }
  }
  // 对离床和非意图段也做一遍碎片合并检查
  for (let i = 0; i < stage_data.nointention?.length; i++) {
    stage_data.nointention[i].s = Math.round(stage_data.nointention[i].s);
    stage_data.nointention[i].e = Math.round(stage_data.nointention[i].e);
  }
  for (let i = 1; i < stage_data.nointention?.length; i++) {
    if ((stage_data.nointention[i - 1].e === stage_data.nointention[i].s) || (stage_data.nointention[i - 1].e + 1 === stage_data.nointention[i].s)) {
      stage_data.nointention[i - 1].e = stage_data.nointention[i].e;
      stage_data.nointention.splice(i, 1);
      i--;
    }
  }
  for (let i = 1; i < stage_data.leavebed?.length; i++) {
    if ((stage_data.leavebed[i - 1].e === stage_data.leavebed[i].s) || ((stage_data.leavebed[i - 1].e + 1 === stage_data.leavebed[i].s))) {
      stage_data.leavebed[i - 1].e = stage_data.leavebed[i].e;
      stage_data.leavebed.splice(i, 1);
      i--;
    }
  }

  // 归一化数据缺失区间
  const signalMissingIntervals = normalizeSignalMissingIntervals(
    stage_data.signalMissingIntervals,
    stage_data.end - stage_data.start,
    stage_data.minMissingSeconds
  );
  // 从离床段中减去数据缺失区间，使二者互斥显示
  const leavebedForDisplay = subtractLeaveBedIntervals(stage_data.leavebed, signalMissingIntervals);

  // 用离床和非意图时间把wake分期拆开
  let hasIntentionData = false;
  let hasLeaveData = false;
  for (let i = 0; i < data.length; i++) {
    if (data[i].value[0] === 3) {
      for (let j = 0; j < stage_data.nointention?.length; j++) {
        const nointent = stage_data.nointention[j];
        const oldname = data[i].name;
        const oldstart = data[i].value[1] / 1000;
        const oldend = data[i].value[2] / 1000;
        const oldstyle = data[i].itemStyle;
        if (baseTime + nointent.s >= oldstart && baseTime + nointent.e <= oldend) { // 如果当前wake分期与意图有重叠
          hasIntentionData = true;
          let idx = i;
          if (baseTime + nointent.s > oldstart) { // 如果有，拆分第一个wake段，并替换掉原来的wake段
            data.splice(i, 1, {
              name: oldname,
              value: [3, oldstart * 1000, baseTime * 1000 + nointent.s * 1000, -1],
              itemStyle: oldstyle
            })
            idx = i + 1;
          } else { // 否则要删掉原来的wake段
            data.splice(i, 1);
          }
          // 插入一个非意图段
          data.splice(idx, 0, {
            name: L.active,
            value: [4, baseTime * 1000 + nointent.s * 1000, baseTime * 1000 + nointent.e * 1000, -1],
            itemStyle: { color: catColor[4] }
          })
          if (baseTime + nointent.e < oldend) { // 如果有，拆分后一个wake段
            data.splice(idx + 1, 0, {
              name: oldname,
              value: [3, baseTime * 1000 + nointent.e * 1000, oldend * 1000, -1],
              itemStyle: oldstyle
            })
          }
        }
      }
    }
  }
  for (let i = 0; i < data.length; i++) {
    if (data[i].value[0] === 3) {
      for (let j = 0; j < leavebedForDisplay.length; j++) {
        const leave = leavebedForDisplay[j];
        const oldname = data[i].name;
        const oldstart = data[i].value[1] / 1000;
        const oldend = data[i].value[2] / 1000;
        const oldstyle = data[i].itemStyle;
        if (baseTime + leave.s >= oldstart && baseTime + leave.e <= oldend) { // 如果当前wake分期与意图有重叠
          hasLeaveData = true;
          let idx = i;
          if (baseTime + leave.s > oldstart) { // 如果有，拆分第一个wake段，并替换掉原来的wake段
            data.splice(i, 1, {
              name: oldname,
              value: [3, oldstart * 1000, baseTime * 1000 + leave.s * 1000, -1],
              itemStyle: oldstyle
            })
            idx = i + 1;
          } else { // 否则要删掉原来的wake段
            data.splice(i, 1);
          }
          // 插入一个离床段
          data.splice(idx, 0, {
            name: L.leave_bed,
            value: [5, baseTime * 1000 + leave.s * 1000, baseTime * 1000 + leave.e * 1000, -1],
            itemStyle: { color: catColor[5] }
          })
          if (baseTime + leave.e < oldend) { // 如果有，拆分后一个wake段
            data.splice(idx + 1, 0, {
              name: oldname,
              value: [3, baseTime * 1000 + leave.e * 1000, oldend * 1000, -1],
              itemStyle: oldstyle
            })
          }
        }
      }
    }
  }

  // 数据缺失与原生小程序的离床一致：拆分清醒段后插入主分期序列，
  // 作为独立矩形绘制，不再使用单独的顶层 overlay series。
  let hasSignalMissing = false;
  for (let i = 0; i < data.length; i++) {
    if (data[i].value[0] === 3) {
      for (const missing of signalMissingIntervals) {
        const oldname = data[i].name;
        const oldstart = data[i].value[1] / 1000;
        const oldend = data[i].value[2] / 1000;
        const oldstyle = data[i].itemStyle;
        if (baseTime + missing.s >= oldstart && baseTime + missing.e <= oldend) {
          hasSignalMissing = true;
          let idx = i;
          if (baseTime + missing.s > oldstart) {
            data.splice(i, 1, {
              name: oldname,
              value: [3, oldstart * 1000, baseTime * 1000 + missing.s * 1000, -1],
              itemStyle: oldstyle
            });
            idx = i + 1;
          } else {
            data.splice(i, 1);
          }
          data.splice(idx, 0, {
            name: L.signal_missing || '数据缺失',
            value: [
              SIGNAL_MISSING_STAGE_INDEX,
              baseTime * 1000 + missing.s * 1000,
              baseTime * 1000 + missing.e * 1000,
              -1,
            ],
            isSignalMissing: true,
            itemStyle: { color: catColor[SIGNAL_MISSING_STAGE_INDEX] },
          });
          if (baseTime + missing.e < oldend) {
            data.splice(idx + 1, 0, {
              name: oldname,
              value: [3, baseTime * 1000 + missing.e * 1000, oldend * 1000, -1],
              itemStyle: oldstyle
            });
          }
        }
      }
    }
  }

  // 在每一段的数据中记住下一个段的分期值
  let firstLegend = null; // 记住第一个出现的需要legend的类型，画legend按出现的顺序来排
  for (let i = 0; i < data.length - 1; i++) {
    if (data[i].value[0] === 4 && !firstLegend) {
      firstLegend = 'intent';
    }
    if (data[i].value[0] == 5 && !firstLegend) {
      firstLegend = 'leave';
    }
    if (data[i + 1].value[1] == data[i].value[2]) // 处理空洞，空洞位置不画连接线
    { data[i].value[3] = data[i + 1].value[0]; } else { data[i].value[3] = -1; }
  }
  // console.log(data);

  const sleepStart = stage_data.start * 1000;
  const sleepEnd = stage_data.end * 1000;

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      triggerOn: 'click',
      backgroundColor: getColor('background'),
      borderColor: 'transparent',
      textStyle: {
        fontSize: 12,
        lineHeight: 9,
        textShadowBlur: 2,
        textShadowColor: 'transparent',
        color: getColor('text')
      },
      formatter: function (params) {
        const len = (params.value[2] - params.value[1]) / 1000;
        const timeRange = formatDateString(params.value[1], 'HHmm') + L.start_word +
                    ' - ' + formatDateString(params.value[2], 'HHmm') + L.end_word;
        if (params.data && params.data.isSignalMissing) {
          return params.marker +
                    (L.signal_missing || '数据缺失') + ': ' + seconds2HHmm(len, true) +
                    '\n' + timeRange +
                    '\n' + (L.signal_missing_description || '网络不稳导致信号丢失\n设备正在补传中，请保持通电。');
        }
        return params.marker +
                    params.name + ': ' + seconds2HHmm(len, true) +
                    '\n' + timeRange;
      },
      // valueFormatter: (value) => formatDateString(value, "HHmm"),
      confine: true,
      shadowBlur: 4
    },
    grid: {
      left: '8%',
      right: '8%',
      top: '20%',  // 增加顶部空间
      bottom: '2%',
      containLabel: true
    },
    legend: {
      data: [
        ...(firstLegend === 'leave'
          ? [hasLeaveData ? L.leave_bed : '', hasIntentionData ? L.active : '']
          : [hasIntentionData ? L.active : '', hasLeaveData ? L.leave_bed : '']),
        hasSignalMissing ? (L.signal_missing || '数据缺失') : '',
      ],
      orient: 'horizontal',
      top: '5%',   // 将图例向上移动
      selectedMode: false,
      itemHeight: 12,
      itemWidth: 8,
      itemGap: 6,
      itemStyle: {
        opacity: 0.9
      },
      textStyle: {
        fontSize: 10
      }
    },
    
    xAxis: {
      min: 'dataMin',
      max: 'dataMax',
      minInterval: 120 * 60000,
      maxInterval: 120 * 60000,
      splitLine: {
        lineStyle: { type: 'dashed' }
      },
      axisLabel: {
        showMinLabel: true,
        showMaxLabel: true,
        formatter: function (val) {
          const tag = formatDateString(val, 'HHmm');
          // if (val % 3600000 != 0)
          if (val === sleepStart || val === sleepEnd) {
            return '{endtag|' + tag + '}';
          } else { return '{all|' + tag + '}'; }
        },
        rich: {
          all: {
            padding: 2
          },
          endtag: {
            color: getColor('text'),
            backgroundColor: getColor('tag'),
            padding: 2,
            borderRadius: 2
          }
        }
      }
    },
    yAxis: {
      data: categories,
      axisTick: {
        show: true,
        inside: false
      },
      axisLabel: {
        inside: true,
        fontSize: 9,
        padding: [-14, 0, 0, -5],
        verticalAlign: 'top'
      },
      z: 10
    },
    dataZoom: {
      type: 'inside',
      start: 0,
      end: 100,
      filterMode: 'weakFilter', // empty会exception
      minSpan: 40,
      maxSpan: 100,
      zoomLock: false,
      orient: 'horizontal'
    },
    series: [{
      type: 'custom',
      clip: false,
      renderItem,
      itemStyle: {
        borderWidth: 0,
        opacity: 0.9,
        borderColor: '#dddddd'
      },
      dimensions: [L.tab_stages, L.start_word, L.end_word],
      encode: {
        x: [1, 2],
        y: 0,
        tooltip: [1, 2]
      },
      data
    }, {
      type: 'custom',
      renderItem,

      name: L.leave_bed,
      itemStyle: {
        color: catColor[5]
      }
    }, {
      type: 'custom',
      renderItem,

      name: L.active,
      itemStyle: {
        color: catColor[4]
      }
    }, {
      // 仅用于图例；实际缺失矩形已经合入主分期序列。
      type: 'custom',
      renderItem, // ECharts requires this even for an empty legend-only custom series.
      name: L.signal_missing || '数据缺失',
      itemStyle: { color: catColor[SIGNAL_MISSING_STAGE_INDEX] },
    }]
  };
  stage_chart.setOption(option);
}
